import {
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  TrashIcon,
  MinusIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { HelpMessageContent } from "./HelpMessageContent";
import helpAssistantService from "../../services/helpAssistantService";
import {
  HELP_OFF_TOPIC_RESPONSE,
  HELP_UNAVAILABLE_RESPONSE,
  isChairsideAppQuestion,
  SUGGESTED_QUESTIONS,
} from "../../lib/helpAssistantResponses";
import { getManualHrefForRole } from "../../config/helpKnowledgeBuilder";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import clsx from "clsx";

const CHAT_LIMIT = 50;
const STORAGE_KEY = "chairsideHelpAssistantMessages";

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full bg-surface-400 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
}

function MessageBubble({ msg, onNavigate }) {
  const isUser = msg.role === "user";
  return (
    <div
      className={clsx("flex w-full mb-3", isUser ? "justify-end" : "justify-start")}
    >
      {!isUser && (
        <div className="mr-2 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-600 shadow-sm">
          <ChatBubbleLeftRightIcon className="h-4 w-4 text-white" />
        </div>
      )}
      <div
        className={clsx(
          "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm whitespace-pre-wrap break-words",
          isUser
            ? "rounded-tr-sm bg-gradient-to-br from-teal-500 to-teal-600 text-white"
            : "rounded-tl-sm bg-white text-surface-700 ring-1 ring-surface-100",
        )}
      >
        <HelpMessageContent
          content={msg.content}
          variant={isUser ? "user" : "assistant"}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}

export default function HelpAssistant() {
  const { user } = useAuth();
  const role = user?.role;
  const manualHref = getManualHrefForRole(role);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) setMessages(JSON.parse(saved));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && !minimized) {
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [open, minimized]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setMinimized(false);
    };
    window.addEventListener("helpAssistant:open", handler);
    return () => window.removeEventListener("helpAssistant:open", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const detect = () => {
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) {
        return true;
      }
      for (const el of document.querySelectorAll(".fixed.inset-0")) {
        const z = parseInt(getComputedStyle(el).zIndex || "0", 10);
        if (z >= 40) return true;
      }
      return false;
    };
    const update = () => setModalOpen(detect());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "role", "aria-modal"],
    });
    return () => observer.disconnect();
  }, []);

  const chatTooLong = messages.length >= CHAT_LIMIT;

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || loading || chatTooLong) return;

      const userMsg = {
        id: `u-${Date.now()}`,
        role: "user",
        content: trimmed,
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setLoading(true);

      const assistantId = `a-${Date.now()}`;

      if (!isChairsideAppQuestion(trimmed)) {
        setMessages((prev) => [
          ...prev,
          { id: assistantId, role: "assistant", content: HELP_OFF_TOPIC_RESPONSE },
        ]);
        setLoading(false);
        return;
      }

      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", content: "" },
      ]);

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const res = await helpAssistantService.streamChat(
          {
            messages: [...messages, userMsg].map((m) => ({
              role: m.role,
              content: m.content,
            })),
          },
          ctrl.signal,
        );

        if (!res.ok || !res.body) {
          let errText = HELP_UNAVAILABLE_RESPONSE;
          try {
            const raw = await res.text();
            if (raw.toLowerCase().includes("chat history too long")) {
              errText = "Chat history too long. Please start a new conversation.";
            } else if (raw.trim()) {
              errText = raw.trim();
            }
          } catch {
            /* ignore */
          }
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: errText } : m,
            ),
          );
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          accumulated += decoder.decode(value, { stream: true });
          const snap = accumulated;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: snap } : m,
            ),
          );
        }

        if (!accumulated.trim()) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: HELP_OFF_TOPIC_RESPONSE }
                : m,
            ),
          );
        }
      } catch (err) {
        if (err?.name !== "AbortError") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: HELP_UNAVAILABLE_RESPONSE }
                : m,
            ),
          );
        }
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    },
    [loading, messages, chatTooLong],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setOpen(false);
    setMinimized(false);
  };

  const handleClear = () => {
    abortRef.current?.abort();
    setMessages([]);
    setLoading(false);
    setInput("");
  };

  const handleHelpNavigate = useCallback(() => {
    setOpen(false);
    setMinimized(false);
  }, []);

  const showSuggestions = messages.length === 0 && !loading;
  const showFab = !open && !modalOpen;

  return (
    <div
      className="fixed bottom-6 right-6 z-[55] flex flex-col items-end gap-3"
      aria-label="Help assistant"
    >
      {open && !minimized && (
        <div
          className="flex flex-col overflow-hidden rounded-2xl bg-surface-50 shadow-[0_8px_40px_rgba(0,0,0,0.18)] ring-1 ring-surface-200 w-[calc(100vw-32px)] h-[calc(100vh-120px)] max-w-[370px] max-h-[540px] sm:w-[370px] sm:h-[540px] animate-slide-in-right"
          role="dialog"
          aria-label="Chairside Help Assistant"
        >
          <div className="flex shrink-0 items-center gap-3 bg-gradient-to-r from-teal-600 to-teal-500 px-4 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">Chairside Assistant</p>
              <div className="flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-green-300" />
                <p className="text-[11px] text-white/80">Online · Here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  title="Clear chat"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 hover:bg-white/20 hover:text-white"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setMinimized(true)}
                title="Minimize"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 hover:bg-white/20 hover:text-white"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleClose}
                title="Close"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 hover:bg-white/20 hover:text-white"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-2 pt-4">
            {messages.length === 0 && (
              <div className="mb-4 flex flex-col items-center text-center">
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 shadow-md">
                  <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
                </div>
                <p className="text-sm font-semibold text-surface-800">
                  Hi! I&apos;m your Chairside Assistant
                </p>
                <p className="mt-1 text-xs text-surface-500">
                  Ask about any feature in Chairside — I know all modules and will tell you which role handles each one.
                </p>
              </div>
            )}

            {showSuggestions && (
              <div className="mb-2">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-surface-400">
                  Quick questions
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => sendMessage(q)}
                      className="rounded-full border border-teal-200 bg-white px-3 py-1 text-xs text-teal-700 shadow-sm hover:border-teal-400 hover:bg-teal-50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id}>
                {msg.content === "" && msg.role === "assistant" && loading ? (
                  <div className="mb-3 flex items-start gap-2">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-600">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 ring-1 ring-surface-100">
                      <TypingDots />
                    </div>
                  </div>
                ) : (
                  <MessageBubble msg={msg} onNavigate={handleHelpNavigate} />
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="shrink-0 border-t border-surface-200 bg-white px-3 pb-2.5 pt-2">
            {chatTooLong ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-center">
                <p className="text-xs font-medium text-amber-800">
                  Chat history too long. Please start a new conversation.
                </p>
                <button
                  type="button"
                  onClick={handleClear}
                  className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                >
                  Start new conversation
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2 rounded-xl border border-surface-200 bg-surface-50 px-2.5 py-1.5 focus-within:border-teal-400 focus-within:ring-2 focus-within:ring-teal-100">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your question…"
                    rows={1}
                    disabled={loading}
                    className="min-h-[32px] flex-1 resize-none border-0 bg-transparent py-1 text-sm text-surface-800 outline-none ring-0 ring-offset-0 placeholder:text-surface-400 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-50"
                    style={{ maxHeight: 80 }}
                  />
                  <button
                    type="button"
                    onClick={() => sendMessage(input)}
                    disabled={loading || !input.trim()}
                    title="Send"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 text-white disabled:opacity-40"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1 text-center text-[10px] text-surface-400">
                  Powered by OpenAI · Answers from the full Chairside guide · Enter to send ·{" "}
                  <Link
                    to={manualHref}
                    onClick={handleClose}
                    className="text-teal-600 hover:underline"
                  >
                    Full manual
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {open && minimized && !modalOpen && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="flex items-center gap-2.5 rounded-2xl bg-gradient-to-r from-teal-600 to-teal-500 px-4 py-2.5 text-white shadow-lg hover:shadow-xl"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Chairside Assistant</span>
        </button>
      )}

      {showFab && (
        <div className="group relative">
          <span className="pointer-events-none absolute bottom-full right-0 mb-2.5 whitespace-nowrap rounded-lg bg-surface-900 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
            Chat with us
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open help assistant"
            className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-[0_4px_20px_rgba(20,184,166,0.45)] transition-all hover:scale-105 hover:shadow-[0_6px_28px_rgba(20,184,166,0.55)]"
          >
            <ChatBubbleLeftRightIcon className="h-7 w-7" />
          </button>
        </div>
      )}
    </div>
  );
}
